import { useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Star } from "lucide-react";
import { useStoreCustomers, useCreateStoreCustomer } from "@/api/pos";
import { getApiErrorMessage } from "@/api/client";

/** Clientes de mostrador de la tienda (distinto de "Clientes" de la firma) — con su saldo de puntos de fidelización, acumulados automáticamente por cada venta (ver pos.service.ts::POINTS_PER_CURRENCY_UNIT). */
export default function StoreCustomersPage() {
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const { data: customers, isLoading } = useStoreCustomers(search || undefined);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Clientes de tienda</h1>
          <p className="text-sm text-gray-500">Clientes de mostrador del POS — acumulan puntos de fidelización con cada compra (1 punto por cada $1.000).</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nuevo cliente
        </button>
      </div>

      <div className="card p-4">
        <input className="input-field" placeholder="Buscar por nombre o documento..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Documento</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Teléfono</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Puntos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && (customers?.length ?? 0) === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                  {search ? "No se encontraron clientes." : "Aún no hay clientes registrados."}
                </td>
              </tr>
            )}
            {customers?.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-900">{c.name}</td>
                <td className="px-4 py-3 text-gray-600">{c.documentId ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{c.phone ?? "—"}</td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1 font-medium text-amber-700">
                    <Star size={13} className="fill-amber-400 text-amber-400" /> {c.pointsBalance}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateCustomerModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function CreateCustomerModal({ onClose }: { onClose: () => void }) {
  const createCustomer = useCreateStoreCustomer();
  const [name, setName] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("El nombre es obligatorio.");
      return;
    }
    try {
      await createCustomer.mutateAsync({ name, documentId: documentId || undefined, phone: phone || undefined, email: email || undefined });
      toast.success("Cliente registrado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nuevo cliente</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Documento (opcional)</label>
            <input className="input-field mt-1" value={documentId} onChange={(e) => setDocumentId(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Teléfono (opcional)</label>
            <input className="input-field mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Correo (opcional)</label>
            <input className="input-field mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={createCustomer.isPending} onClick={handleSubmit}>
            {createCustomer.isPending ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
